import { Image, makeStyles, mergeClasses, Skeleton, SkeletonItem, Text, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import testimonialService from "../../services/TestimonialService";
import ITestimonial from "../../models/testimonial/ITestimonial";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Status from "../../enums/Status";
import { dolphinTokens } from "../../utils/Theme";
import { HtmlToPlainText } from "../../utils/Html";

const useStyles = makeStyles({
    container: {
        marginBottom: "3rem"
    },
    sectionHeader: {
        textAlign: "center"
    },
    blueBadge: {
        backgroundColor: dolphinTokens.colorLightBlue,
        color: "white",
        padding: "0.5rem 1.5rem",
        borderRadius: "50px",
        fontSize: "0.875rem",
        textTransform: "uppercase",
        display: "inline-block"
    },
    reviewCarousel: {
        position: "relative",
        padding: "2rem",
        display: "flex",
        justifyContent: "center"
    },
    reviewSlide: {
        display: "none",
        opacity: 0,
        transition: "opacity 0.5s ease-in-out",
        textAlign: "center",
        width: "75%",
        
        "&.active": {
            display: "block !important",
            opacity: "1 !important"
        },

        "& a": {
            color: dolphinTokens.colorLightBlue,
            textDecoration: "none"
        },

        "@media (max-width: 768px)": {
            width: "90%"
        },
        "& img": {
            height: "128px",
            width: "auto",
        }
    },
    skeletons: {
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,

        "& > div": {
            marginLeft: "auto",
            marginRight: "auto"
        },

        "& > :nth-child(1)": {
            width: "256px !important",
            marginBottom: "1.5rem !important",

        },

        "& > :nth-child(2)": {
            width: "70% !important",

        }
    }
});

const Testimonial = (props: { testimonial: ITestimonial, active: boolean }) => {
    const { testimonial, active } = props;
    
    const { t } = useTranslation("home");
    const styles = useStyles();

    const className = active
        ? mergeClasses(styles.reviewSlide, "active")
        : styles.reviewSlide;

    const content = HtmlToPlainText(testimonial.content);

    return (
        <div className={className}>
            <Image
                src={testimonial.client.logo.url}
                alt={testimonial.client.logo.altText}
                fetchPriority="low"
                loading="lazy"
            />
            
            <Text block size={700} align="center">
                &ldquo;
                {
                    content.length < 170
                        ? <span>{content} </span>
                        : <span>{content.substring(0, 170)}... </span>
                }
                <Link to="/testimonials">
                    {
                        content.length > 170
                            ? t("testimonials.btnReadMore")
                            : t("testimonials.btnReadAll")
                    }
                </Link>
                &rdquo;
            </Text>
            
            <Text as="p" size={400}>
                {testimonial.client.name}
            </Text>
        </div>
    );
};

export default function Testimonials() {
    const { t } = useTranslation("home");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [testimonials, setTestimonials] = useState<ITestimonial[]>([]);
    const [activeTestimonial, setActiveTestimonial] = useState<number>(0);

    useEffect(() => {
        getTestimonials(setTestimonials, setStatus);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTestimonial((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
        }, 5000);
    
        return () => clearInterval(interval);
    }, [testimonials.length]);

    if (status === Status.LOAD_ERROR) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={styles.sectionHeader}>
                <Text as="span" className={styles.blueBadge}>
                    {t('testimonials.pill')}
                </Text>
            </div>
            <Text as="h2" size={700} align="center" block>
                {t('testimonials.title')}
            </Text>
            
            {
                status === Status.LOADING &&
                <div className={styles.skeletons}>
                    <Skeleton>
                        <SkeletonItem size={128} shape="rectangle" />
                    </Skeleton>
                    <Skeleton>
                        <SkeletonItem size={72} shape="rectangle" />
                    </Skeleton>
                </div>
            }
            
            {
                status === Status.LOAD_SUCCESS &&
                <div className={styles.reviewCarousel}>
                    {
                        testimonials
                            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                            .map((testimonial, index) => (
                                <Testimonial
                                    key={testimonial.id}
                                    testimonial={testimonial}
                                    active={index === activeTestimonial}
                                />
                            ))
                    }
                </div>
            }
        </div>
    );
}

async function getTestimonials(
    setTestimonials: (caseStudies: ITestimonial[]) => void,
    setStatus: (status: Status) => void
) {
    try {
        const response = await testimonialService.getTestimonials();
        
        setTestimonials(response);
        setStatus(Status.LOAD_SUCCESS);
    } catch (_) {
        setStatus(Status.LOAD_ERROR);
    }
}